/**
 * Cross-game conflict banner overlay (Card Match copy of the standard
 * platform component used by anagram / who-am-i / horse-race / survey-says
 * / whatis).
 *
 * Shown when the backend broadcasts `cross_game_conflict`: another game is
 * mid-play for this tenant and someone tried to start a different one.
 * Backend already sends a chat reply + 409 to the dashboard; this surfaces
 * the same warning on the game's browser source so the streamer (looking
 * at OBS) actually sees it.
 *
 * Display rules:
 *   - Top-center, below any top-bar elements (z-index 95).
 *   - Yellow background (`var(--sg-primary)`) + deep-blue text
 *     (`var(--sg-text-on-primary)`), so per-tenant theming applies.
 *   - Auto-dismisses after 8s; smooth ~300ms fade in/out.
 *   - Non-blocking: pointer-events: none. Replaces an in-flight banner if
 *     a second event lands so the latest message wins.
 */

const STYLE_ID = 'sg-conflict-banner-style';

const STYLES = `
  @keyframes sg-conflict-banner-in {
    from { opacity: 0; transform: translate(-50%, -12px); }
    to   { opacity: 1; transform: translate(-50%, 0); }
  }
  @keyframes sg-conflict-banner-out {
    from { opacity: 1; transform: translate(-50%, 0); }
    to   { opacity: 0; transform: translate(-50%, -12px); }
  }
  .sg-conflict-banner {
    position: absolute;
    top: 80px;
    left: 50%;
    transform: translate(-50%, 0);
    width: 640px;
    max-width: 80%;
    padding: 16px 24px;
    border-radius: 12px;
    background: var(--sg-primary, #FFC43D);
    color: var(--sg-text-on-primary, #060CE9);
    font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
    font-size: 22px;
    line-height: 1.3;
    font-weight: 600;
    text-align: center;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
    z-index: 95;
    pointer-events: none;
    animation: sg-conflict-banner-in 300ms cubic-bezier(.22,1,.36,1) both;
  }
  .sg-conflict-banner.sg-conflict-banner-leaving {
    animation: sg-conflict-banner-out 300ms ease-in both;
  }
  .sg-conflict-banner-emoji {
    margin-right: 8px;
  }
`;

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = STYLES;
  document.head.appendChild(s);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export interface ConflictBannerHandle {
  /** Show the banner. Replaces any in-flight banner. Auto-dismisses in ~8s. */
  show: (message: string) => void;
  /** Force-dismiss immediately (no fade). Mostly for tests / scene tearing-down. */
  dismiss: () => void;
}

const VISIBLE_MS = 8_000;
const FADE_OUT_MS = 300;

export function createConflictBanner(mount: HTMLElement): ConflictBannerHandle {
  ensureStyles();
  let active: HTMLDivElement | null = null;
  let visibleTimer: number | null = null;
  let leaveTimer: number | null = null;

  function clearTimers(): void {
    if (visibleTimer !== null) {
      clearTimeout(visibleTimer);
      visibleTimer = null;
    }
    if (leaveTimer !== null) {
      clearTimeout(leaveTimer);
      leaveTimer = null;
    }
  }

  function dismiss(): void {
    clearTimers();
    if (active) active.remove();
    active = null;
  }

  function show(message: string): void {
    // Replace any in-flight banner so back-to-back conflicts don't stack and
    // the latest message wins.
    dismiss();
    const el = document.createElement('div');
    el.className = 'sg-conflict-banner';
    el.setAttribute('data-overlay', 'conflict-banner');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML = `<span class="sg-conflict-banner-emoji">⚠️</span>${escapeHtml(message)}`;
    mount.appendChild(el);
    active = el;
    visibleTimer = window.setTimeout(() => {
      visibleTimer = null;
      if (!active) return;
      active.classList.add('sg-conflict-banner-leaving');
      const target = active;
      leaveTimer = window.setTimeout(() => {
        leaveTimer = null;
        target.remove();
        if (active === target) active = null;
      }, FADE_OUT_MS);
    }, VISIBLE_MS);
  }

  return { show, dismiss };
}
